import { Component, ViewChild, computed, signal, Signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  DsButtonGroupModule,
  DsButtonModule,
  DsButtonVariants,
  DsListMenuItem,
  DsModalModule,
  DsModalVariant,
  DsMenuModule,
  Icons,
} from '@levelaccess/design-system';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CommonPipesModule } from '@client/app/shared/common-pipes.module';
import { TranslateService } from '@client/app/translate/translate.service';
import { AccountProfileComponent } from '../profile/account-profile.component';
import { AccountProfileModule } from '../profile/account-profile.module';
import { AccountSettingsComponent } from '../settings/account-settings.component';
import { AccountSettingsModule } from '../settings/account-settings.module';

export type AccountSettingsSection = 'profile' | 'account';

@Component({
  selector: 'app-account-settings-modal',
  templateUrl: './account-settings-modal.component.html',
  styleUrl: './account-settings-modal.component.scss',
  imports: [
    CommonModule,
    CommonPipesModule,
    DsModalModule,
    DsButtonModule,
    DsButtonGroupModule,
    DsMenuModule,
    AccountProfileModule,
    AccountSettingsModule,
  ],
  standalone: true,
})
export class AccountSettingsModalComponent {
  readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  readonly DsModalVariant: typeof DsModalVariant = DsModalVariant;
  readonly Icons: typeof Icons = Icons;

  readonly activeSection: WritableSignal<AccountSettingsSection> = signal<AccountSettingsSection>('profile');

  readonly menuItems: Signal<DsListMenuItem[]> = computed((): DsListMenuItem[] => {
    const section = this.activeSection();
    const profileLabel = this.translate.instant('profile');
    const accountLabel = this.translate.instant('sidebar_menu_account');
    return [
      {
        id: 'profile',
        label: profileLabel,
        icon: Icons.User,
        active: section === 'profile',
        onClick: (): void => this.activeSection.set('profile'),
      },
      {
        id: 'account',
        label: accountLabel,
        icon: Icons.Key,
        active: section === 'account',
        onClick: (): void => this.activeSection.set('account'),
      },
    ];
  });

  @ViewChild(AccountProfileComponent) profileRef?: AccountProfileComponent;
  @ViewChild(AccountSettingsComponent) settingsRef?: AccountSettingsComponent;

  private savingSections: Map<string, boolean> = new Map<string, boolean>();

  constructor(
    public readonly modal: NgbActiveModal,
    private translate: TranslateService,
  ) {}

  private shouldProfileSave(): boolean {
    return this.profileRef?.form?.dirty;
  }

  private shouldSettingsSave(): boolean {
    return this.settingsRef?.arePasswordFieldsEmpty() === false;
  }

  onSave(): void {
    if (this.shouldProfileSave()) {
      this.profileRef?.onSubmit();
      this.savingSections.set('profile', true);
    }
    if (this.shouldSettingsSave()) {
      this.settingsRef?.submitChangePassword();
      this.savingSections.set('account', true);
    }
    if (this.savingSections.size === 0) {
      this.modal.close();
    }
  }

  onCancel(): void {
    this.modal.dismiss();
  }

  onPasswordChangeSuccess(): void {
    this.savingSections.delete('account');
    if (this.savingSections.size === 0) {
      this.modal.close();
    }
  }

  onProfileSaveSuccess(): void {
    this.savingSections.delete('profile');
    if (this.savingSections.size === 0) {
      this.modal.close();
    }
  }

  onProfileSaveError(): void {
    this.activeSection.set('profile');
  }

  onPasswordChangeError(): void {
    this.activeSection.set('account');
  }
}
